import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { Newspaper, AlertTriangle, ExternalLink, Calendar, Info } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";

const SEVERITY = {
  warning: { color: "#FBBF24", icon: AlertTriangle },
  info: { color: "#60A5FA", icon: Info },
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * ATO Rulings feed + last-reviewed footer. Replaces the "TAX ALERT PUSH" v1 —
 * for now we surface these in-app; real Web Push notifications are backlogged.
 */
export default function AtoRulingsCard() {
  const [rulings, setRulings] = useState([]);
  const [reviewedAt, setReviewedAt] = useState(null);
  const [nextDue, setNextDue] = useState(null);
  const [reviewDue, setReviewDue] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    let mounted = true;
    api
      .get("/tax/rulings")
      .then(({ data }) => {
        if (!mounted) return;
        setRulings(data.rulings || []);
        setReviewedAt(data.reviewed_at);
        setNextDue(data.next_review_due);
        setReviewDue(!!data.review_due);
      })
      .catch((err) => devLog.warn("rulings load failed", err));
    return () => {
      mounted = false;
    };
  }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="ato-rulings">
      <div className="flex items-center gap-2 text-[#FF9500]">
        <Newspaper className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-heading">Tax Rulings Feed</span>
      </div>
      <div className="font-heading font-extrabold text-lg leading-tight">
        What&apos;s changed for tradies
      </div>

      {reviewDue && (
        <div className="rounded-lg border border-[#FBBF24]/40 bg-[#FBBF24]/10 px-3 py-2 text-[11px] text-[#FBBF24] inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" />
          Ruleset is due for a review — figures shown may be out of date.
        </div>
      )}

      <div className="space-y-2" data-testid="rulings-list">
        {rulings.map((r) => {
          const sev = SEVERITY[r.severity] || SEVERITY.info;
          const Icon = sev.icon;
          const isOpen = expanded.has(r.id);
          return (
            <button
              key={r.id}
              type="button"
              data-testid={`ruling-${r.id}`}
              onClick={() => toggle(r.id)}
              className="w-full text-left rounded-lg p-3 bi-surface-2 border border-white/5 hover:border-white/10 transition"
            >
              <div className="flex items-start gap-2">
                <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: sev.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 font-heading inline-flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" /> {fmtDate(r.date)}
                  </div>
                  <div className="mt-0.5 font-semibold text-sm leading-snug text-white/90">
                    {r.title}
                  </div>
                  {isOpen && (
                    <div className="mt-2 text-[12px] text-white/70 leading-snug">{r.summary}</div>
                  )}
                  {isOpen && r.source_url && (
                    <a
                      href={r.source_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#FF9500] hover:underline"
                    >
                      Open at ato.gov.au <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {!isOpen && r.affects?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.affects.slice(0, 3).map((a) => (
                        <span
                          key={a}
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/50"
                        >
                          {a.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {reviewedAt && (
        <div className="text-[10px] text-white/40 pt-1 border-t border-white/5">
          Curated feed · reviewed {fmtDate(reviewedAt)}
          {nextDue && <> · next review due {fmtDate(nextDue)}</>}
        </div>
      )}

      <TaxDisclaimer compact />
    </section>
  );
}
